import { AddressLink, Web3ContactType } from 'Modules'
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router'

import abiPTO from 'abi/abi-pto.json'
import { AppAdmin } from 'api/app-admin'
import { DECIMALS_WEI_USDC } from 'constants/number'
import { mapRentalPeriodsFromApi } from 'extends/pto.extend'
import { useContractViem } from 'hooks/contract/use-contract-viem'
import { convertUnitsContract, parserPercentContract } from 'utils/contract'
import { safeDivide } from 'utils/formatted-number'
import { logError } from 'utils/logger'

interface PTOContextType extends Web3ContactType {
  code?: string | null
  loading?: boolean
  data?: any
  ptoDetail?: any
  contractAddress?: string | null | undefined
  getPTODetail: () => void
}

const PTOContext = createContext<PTOContextType>({
  code: null,
  loading: false,
  data: {},
  ptoDetail: null,
  contractAddress: null,
  contract: null,
  getPTODetail: () => {},
})

interface PTOContextProps {
  children: ReactNode
}

const PTOProvider: React.FC<PTOContextProps> = ({ children }) => {
  const locationParam = useParams()

  const code = locationParam.code || ''

  const [loading] = useState(false)

  const [data, setData] = useState<any>({})

  const [ptoDetail, setPTODetail] = useState<any>(null)

  const [contractAddress, setContractAddress] = useState<AddressLink | null>(null)

  const contract = useContractViem(contractAddress, abiPTO)

  const getPTODetail = useCallback(async () => {
    try {
      const detailResponse = await AppAdmin.getPTODetail(code)
      if (detailResponse.data.success) {
        const details = {
          ...detailResponse.data.data,
          goldenTickets: detailResponse.data.data.goldenTickets.map((t: any) => ({
            ...t,
            inRewardPool: undefined,
          })),
          // API returns rewardsPerNFT in token base units - expose it in human units
          revenueSharingRental: mapRentalPeriodsFromApi(
            detailResponse.data.data.revenueSharingRental
          ),
        }
        setPTODetail(details)
        setContractAddress(details?.contractAddress)
      }
    } catch (error) {
      setPTODetail(null)
      setContractAddress(null)
      logError('Error fetching details: ', error)
    }
  }, [code])

  const getContractInfo = useCallback(async () => {
    try {
      if (contract) {
        const [resultSc, rsTotalLoanAmountRaised, totalSupply, contractBalance] = await Promise.all(
          [
            contract.read.getContractInfo(),
            contract.read.totalLoanAmountRaised(),
            contract.read.totalSupply(),
            contract.read.balanceOf([contractAddress as string]),
          ]
        )
        if (resultSc && resultSc.length > 0) {
          const info = resultSc[2]
          const totalNFTsPurchase = resultSc[4]
          const maxNFTSupply = Number(info?.maxNFTSupply || 0)
          const pricePerNFT = convertUnitsContract(info?.pricePerNFT)
          const buyingFeeBP = parserPercentContract(info?.buyingFeeBP || 0)
          const fundingTarget = maxNFTSupply * pricePerNFT
          const totalLoanAmountRaised = rsTotalLoanAmountRaised
            ? convertUnitsContract(rsTotalLoanAmountRaised, DECIMALS_WEI_USDC)
            : 0
          const percent = safeDivide(totalLoanAmountRaised, fundingTarget) * 100

          // Calculate totalNFTsClaim = totalSupply - contractBalance
          const totalSupplyNumber = Number(totalSupply || 0)
          const contractBalanceNumber = Number(contractBalance || 0)
          const totalNFTsClaim = totalSupplyNumber - contractBalanceNumber

          setData({
            percent,
            fundingTarget,
            buyingFeeBP,
            maxNFTSupply,
            pricePerNFT,
            totalNFTsPurchase: Number(totalNFTsPurchase || 0),
            totalNFTsClaim,
            totalLoanAmountRaised,
            totalSupply: totalSupplyNumber,
            contractBalance: contractBalanceNumber,
          })
        }
      }
    } catch (error) {
      setData(null)
      logError('Error fetching details: ', error)
    }
  }, [contract, contractAddress])

  useEffect(() => {
    getPTODetail()
  }, [code, getPTODetail])

  useEffect(() => {
    getContractInfo()
  }, [contractAddress, getContractInfo])

  return (
    <PTOContext.Provider
      value={{
        code,
        loading,
        data,
        ptoDetail,
        contractAddress,
        contract,
        getPTODetail,
      }}
    >
      {children}
    </PTOContext.Provider>
  )
}

const usePTOContext = (): PTOContextType => {
  const context = useContext(PTOContext)

  if (context === undefined) {
    throw new Error('Context not available')
  }

  return context
}

export { PTOProvider, usePTOContext }
